import { Bell } from 'lucide-react'
import { useScrollReveal } from '../../hooks/useScrollReveal.js'
import { useParallax } from '../../hooks/useParallax.js'

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

/* ─── Data ──────────────────────────────────────────────────── */
const URGENCY_TIERS = [
  {
    label: 'Now',
    expiry: '1 hour',
    desc: 'Providers reply within the hour.',
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    dot: 'bg-red-400',
  },
  {
    label: 'Today',
    expiry: '6 hours',
    desc: 'Providers respond the same day.',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    dot: 'bg-amber-400',
  },
  {
    label: 'Flexible',
    expiry: '24 hours',
    desc: 'Whenever works best for both of you.',
    bg: 'bg-primary-light',
    border: 'border-primary/20',
    text: 'text-primary',
    dot: 'bg-primary',
  },
]

/* ─── Store button (Coming Soon — disabled, branded) ───────── */
function StoreButton({ store }) {
  const isApple = store === 'apple'

  return (
    <div
      role="button"
      aria-disabled="true"
      aria-label={`${isApple ? 'App Store' : 'Google Play'} — Coming Soon`}
      className="relative inline-flex items-center gap-3 px-5 py-3 rounded-xl
                 bg-white/5 border border-dashed border-white/25 cursor-not-allowed
                 text-white/70 select-none"
    >
      {/* Coming Soon badge */}
      <span className="absolute -top-2 -right-2 inline-flex items-center
                       px-2 py-0.5 rounded-full bg-accent text-[9px] font-bold
                       text-white tracking-wider uppercase shadow-md
                       whitespace-nowrap">
        Coming Soon
      </span>
      {/* Platform icon */}
      {isApple ? (
        <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24"
          fill="currentColor" aria-hidden="true">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53
                   0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39
                   c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07
                   3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02
                   2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46
                   2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95
                   1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
      ) : (
        <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24"
          fill="currentColor" aria-hidden="true">
          <path d="M3.18 23.76a1.5 1.5 0 001.64-.17l12.44-7.18-2.77-2.77zM.5
                   1.06A1.5 1.5 0 000 2.25v19.5a1.5 1.5 0 00.5 1.19l.08.07
                   10.93-10.93v-.26L.58.99zm17.18 12.28l-3.1-3.1 3.1-3.1
                   3.49 2.01a1.5 1.5 0 010 2.6l-3.49 1.99zM4.82.41L17.26
                   7.59l-2.77 2.77L2.06.59A1.5 1.5 0 014.82.41z" />
        </svg>
      )}

      <span className="flex flex-col leading-tight text-left">
        <span className="text-[10px] font-normal text-white/60">
          {isApple ? 'Download on the' : 'Get it on'}
        </span>
        <span className="text-[15px] font-bold">
          {isApple ? 'App Store' : 'Google Play'}
        </span>
      </span>
    </div>
  )
}

/* ─── Section ───────────────────────────────────────────────── */
export default function DownloadApp() {
  const leftRef = useScrollReveal()
  const rightRef = useScrollReveal({ threshold: 0.1 })
  const bgOrbRef = useParallax({ speed: 0.28 })

  return (
    <section id="download" className="py-24 bg-primary relative overflow-hidden">
      {/* Parallax ambient orb */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div
          ref={bgOrbRef}
          className="parallax-layer absolute -top-32 -left-32 w-[520px] h-[520px]
                     rounded-full bg-white/5 blur-[100px]"
        />
      </div>
      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* ── Left: CTA ───────────────────────────── */}
          <div ref={leftRef} className="reveal-left text-white text-center sm:text-left flex flex-col items-center sm:items-start">
            <p className="eyebrow text-white/60 mb-4 justify-center sm:justify-start">
              <span className="w-5 h-px bg-white/40" />
              Download the App
            </p>
            <h2
              className="font-extrabold text-white mb-4"
              style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.75rem)' }}
            >
              Need something?<br />
              <span className="text-accent">Ask DecidA.</span>
            </h2>
            <p className="text-base text-white/70 leading-relaxed mb-6 max-w-sm">
              Tell us what you need, your budget, and where you are.
              We find the right providers nearby. Their offers come back to you in the app. You choose.
            </p>

            {/* Primary CTA — drives to waitlist */}
            <button
              onClick={() => scrollTo('waitlist')}
              className="btn-shimmer inline-flex items-center gap-2 px-7 py-3.5
                         bg-accent hover:bg-accent-dark text-white text-sm font-semibold
                         rounded-xl transition-all duration-200 shadow-lg
                         hover:scale-[1.03] active:scale-[0.98] mb-6"
            >
              <Bell className="w-4 h-4" aria-hidden="true" />
              Join the Waitlist
            </button>

            <p className="text-xs text-white/60 mb-3 font-medium">
              Apps launching shortly, join the waitlist to be notified first.
            </p>

            <div className="grid grid-cols-2 sm:flex sm:flex-row gap-3">
              <StoreButton store="apple" />
              <StoreButton store="google" />
            </div>
          </div>

          {/* ── Right: urgency tiers ─────────────────── */}
          <div ref={rightRef} className="reveal-right bg-white rounded-2xl p-7 shadow-card-md">
            <p className="eyebrow mb-4">
              <span className="w-5 h-px bg-primary" />
              How fast do you need it?
            </p>
            <p className="text-sm text-brand-gray mb-6 leading-relaxed">
              Pick how fast you need it. Providers respond within that window.
            </p>
            <div className="flex flex-col gap-3">
              {URGENCY_TIERS.map(({ label, expiry, desc, bg, border, text, dot }, i) => (
                <div
                  key={label}
                  className={`flex items-center gap-4 rounded-xl border px-5 py-4
                              transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm
                              ${bg} ${border}`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold ${text}`}>{label}</p>
                    <p className="text-xs text-brand-gray mt-0.5">{desc}</p>
                  </div>
                  <span className={`text-xs font-semibold flex-shrink-0 ${text}`}>
                    {expiry}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs text-brand-gray">
              If no one responds in time, your request closes and you can send a new one.
            </p>
          </div>

        </div>
      </div>
    </section>
  )
}
